/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2014, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'./UrlWidget',
	'./ScalingUtil',
	'../../../app/nls/StringResources',
	'jquery',
	'underscore',
	'doT',
	'text!./ImageUri.html'
], function (UrlWidget, ScalingUtil, resources, $, _, dot, Template) {


	const mediaImageRegex = /^v[1-9]\/ext\//;

	const ImageWidget = UrlWidget.extend({

		init: function (options) {
			ImageWidget.inherited('init', this, arguments);
			this.model = this.model || {};
			this.model.imageLink = options && options.initialConfigJSON && options.initialConfigJSON.imageLink;
			this.model.altText = options && options.initialConfigJSON && options.initialConfigJSON.altText;
			this.model.fillColor = options && options.initialConfigJSON && options.initialConfigJSON.fillColor;
			this.model.borderColor = options && options.initialConfigJSON && options.initialConfigJSON.borderColor;
			this.propertyCallbacks = this.dashboardApi.getFeature('PropertyCallbacks');
		},


		onContainerReady: function () {
			ImageWidget.inherited('onContainerReady', this, arguments);
			if (this.model) {
				this.model.on('change:imageLink', this._onPropertyChange, this);
				this.model.on('change:altText', this._onPropertyChange, this);
				this.model.on('change:resize', this._onPropertyChange, this);
				this.addWhiteListAttrs('imageLink', 'altText', 'resize');
			}
			if (this.model.fillColor) {
				this.$el.find('div.staticContent').addClass('fill-' + this.model.fillColor);
			}
			if (this.model.borderColor) {
				this.$el.find('div.staticContent').addClass('border-' + this.model.borderColor);
			}
		},

		render: function() {
			if (this.propertyCallbacks.validateImageInput(null, this.model.imageLink).isValid) {
				this.model.imageLink = this.encodeURL(this.model.imageLink);
				this.$el.append(this.getHtmlRender());
			} else {
				this.$el.append(UrlWidget.getDefaultConsumeMarkup(resources.get('imgMissingUrl')));
			}
		},

		setUrl: function (url, errorData = {}) {
			let validateInfo = this.propertyCallbacks.validateImageInput(null, url);
			errorData.message = validateInfo.message;
			if (validateInfo.isValid) {
				const transactionId = _.uniqueId('_addImage_');
				this.set({
					imageLink: url
				}, {
					sender: this,
					payloadData: {
						undoRedoTransactionId: transactionId
					}
				});
				this._initWidthHeight(url, transactionId);
				return true;
			}
			return false;
		},

		_initWidthHeight: function (url, transactionId) {
			return new Promise((resolve, reject) => {
				try {
					// eslint-disable-next-line no-undef
					let img = new Image();
					img.onload = () => {
						img = ScalingUtil.scale({
							image: img,
							max: {
								width: 500,
								height: 500
							},
							format: 'css'
						});
						const $chrome = this.getWidgetStyleNode().closest('.widget');
						$chrome.width(img.width);
						$chrome.height(img.height);
						this.widgetChromeEventRouter.trigger('widget:updateLayout', {
							sender: this,
							payloadData: {
								undoRedoTransactionId: transactionId
							}
						});
						resolve();
					};
					img.src = url;
				} catch (error) {
					reject(error);
				}
			});
		},

		_urlChange: function (options) {
			return (this.setUrl(options.item));
		},

		onEnterContainer: function () {
			const mediaInput = this.$el.find('.inlineEditInput');
			mediaInput.focus();
		},

		isInlineEditMode: function () {
			return !this.model.imageLink;
		},

		getEmptyStateLabel: function(){
			return resources.get('noImgSelected');
		},

		getEmptyStateUrlLabel: function(){
			return resources.get('imgUrl');
		},

		getEmptyStateHelperLabel: function() {
			return resources.get('imgPasteLink');
		},

		getInlineEditCaption: function () {
			return resources.get('enterImgUrl');
		},

		getMissingUrlText: function () {
			return resources.get('imgMissingUrl');
		},

		getAriaLabelText: function () {
			return resources.get('mediaAriaLabel');
		},

		getLabel: function () {
			return this._getAltText() || resources.get('imageWidgetTitle');
		},

		getContentClass: function() {
			return 'imageWidgetContent';
		},

		_getAltText: function () {
			return this.model && this.model.get ? this.model.get('altText') : null;
		},

		_onPropertyChange: function (options) {
			let updatedHtml = null;
			if (this.model.imageLink) {
				this.model.imageLink = this.encodeURL(this.model.imageLink);
				if (this.propertyCallbacks.validateImageInput(null, this.model.imageLink).isValid) {
					const $node = this.getWidgetStyleNode();
					$node.find('input').remove();
					$node.removeClass('inlineEdit');
					let $img = $node.find('img');
					if (!$img.length) {
						$img = $('<img></img>');
						$node.empty();
						$node.append($img);
					}
					$img.attr('src', this.model.imageLink);
					const altText = this._getAltText();
					if (altText) {
						$img.attr('alt', altText);
					} else {
						$img.removeAttr('alt');
					}

					const heightWidth = (this.model.resize === 'false') ? 'auto' : '100%';
					$img.css('width', heightWidth).css('height', heightWidth);
				}
			} else {
				this.renderInlineEditUi();
				updatedHtml = UrlWidget.getDefaultConsumeMarkup(resources.get('imgMissingUrl'));
			}

			this.updateMarkup();
			if (options.data && options.data.undoRedoTransactionId) {
				this.updateModelContent(updatedHtml, options.data.undoRedoTransactionId);
			} else {
				this.updateModelContent(updatedHtml);
			}
		},

		getPropertyList: function() {
			const propertyList = ImageWidget.inherited('getPropertyList', this, arguments) || [];
			// Make the image link field read-only if the image came from an image library.
			_.forEach(propertyList, (property) => {
				if (property.id === 'imageLink' && mediaImageRegex.test(this.model.imageLink)) {
					property.editor.readOnly = true;
					return property;
				}
			});
			return propertyList;
		},

		getHtmlRender: function () {
			const renderTemplate = dot.template(Template);

			const props = {
				url: this.model.imageLink
			};
			const altText = this._getAltText();
			if (altText) {
				props.altText = altText;
			}
			if (this.model.fillColor) {
				props.fillColor = 'fill-' + this.model.fillColor;
			}
			if (this.model.borderColor) {
				props.borderColor = 'border-' + this.model.borderColor;
			}
			if (this.model.imageLink) {
				props.url = this.model.imageLink;
			}

			return renderTemplate(props);
		}
	});

	ImageWidget.getDefaultSpec = function (name, options) {

		options = options || {};

		const spec = {
			model: {
				type: 'image',
				'avatarHtml': UrlWidget.getDefaultConsumeMarkup(),
				'imageLink': options.imageLink,
				'altText': options.altText
			},
			layoutProperties: {
				style: {
					width: '480px',
					height: '360px'
				}
			}
		};

		if (options.imageLink) {
			return new Promise((resolve, reject) => {
				try {
					// eslint-disable-next-line no-undef
					const img = new Image();
					img.onload = function () {
						const dX = 480 / (img.naturalWidth || 1);
						const dY = 360 / (img.naturalHeight || 1);
						if (dX < 1 || dY < 1) {
							spec.layoutProperties.style = {
								height: (Math.min(dX, dY) * img.naturalHeight) + 'px',
								width: (Math.min(dX, dY) * img.naturalWidth) + 'px'
							};
						} else {
							spec.layoutProperties.style = {
								height: img.naturalHeight + 'px',
								width: img.naturalWidth + 'px'
							};
						}
						resolve(spec);
					};
					img.onerror = function () {
						reject();
					};
					img.src = options.imageLink;
				} catch (error) {
					reject(error);
				}
			});
		} else {
			return Promise.resolve(spec);
		}
	};

	return ImageWidget;
});
